import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../utils/networking";
import { useViewport } from "../../utils/useViewport";
import ReferralStatusChip, {
  REFERRAL_PIPELINE_STATUSES,
  pipelineStatusLabels,
  pipelineStatusColors,
} from "../common/ReferralStatusChip";

const PRIMARY = "#9E1F63FF";

// "Days in pipeline" buckets. The dashboard's bounded referrals lane links in
// with `?minDays=90` for everything it had to drop.
const MIN_DAYS_OPTIONS = ["30", "90", "180", "365"];

// Pseudo status: referrals still moving through the pipeline. Same status set
// as the dashboard's Referrals lane (server `REFERRAL_LANE_STATUSES`), so the
// "View N older referrals" deep link lists exactly the rows the lane hid.
const ACTIVE_STATUS_FILTER = "in_pipeline";
const ACTIVE_STATUS_LABEL = "In Pipeline (Referred / Appt. Scheduled / In Progress)";
const IN_PIPELINE_STATUSES = ["referred", "appointment_scheduled", "in_progress"];

const statusFilterLabel = (value: string) =>
  value === ACTIVE_STATUS_FILTER
    ? ACTIVE_STATUS_LABEL
    : pipelineStatusLabels[value] || value;

// Days since the referral was made (`created_at`), the same clock as the
// dashboard's Referrals lane and its "Nd in pipeline" caption.
const daysInPipeline = (referral: any) => {
  const raw = referral?.created_at || referral?.updated_at;
  if (!raw) return 0;
  const parsed = dayjs(raw);
  return parsed.isValid() ? dayjs().diff(parsed, "day") : 0;
};

const typeLabel = (referral: any) =>
  referral.referral_type === "Provider" ? "B2B" : "Resource";

const referralDateLabel = (referral: any) =>
  referral.referral_date || referral.created_at
    ? new Date(
        referral.referral_date || referral.created_at,
      ).toLocaleDateString("en-US")
    : "—";

const caseLink = (referral: any) =>
  `/case?id=${referral.case_id}&referral=${referral._id}`;

// Tablets (below 1200 px): the business name stays in view while the table
// scrolls sideways.
const stickyColumnSx = (theme: Theme) => ({
  [theme.breakpoints.down("lg")]: {
    position: "sticky" as const,
    left: 0,
    zIndex: 1,
    backgroundColor: "#fff",
    boxShadow: "inset -1px 0 0 #ececf0",
    "tr:hover > &": { backgroundColor: "#f5f5f5" },
  },
});

const MyReferrals = () => {
  const { isPhone } = useViewport();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMinDays = searchParams.get("minDays") || "";
  const initialStatus = searchParams.get("status") || "";

  const [referrals, setReferrals] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // A `?minDays=` deep link without an explicit `?status=` means "old and
  // still open", so it defaults the status filter to Active.
  const [statusFilter, setStatusFilter] = useState<string>(
    () =>
      initialStatus ||
      (initialMinDays ? ACTIVE_STATUS_FILTER : "all"),
  );
  const [minDaysFilter, setMinDaysFilter] = useState<string>(() =>
    MIN_DAYS_OPTIONS.includes(initialMinDays) ? initialMinDays : "all",
  );
  const [search, setSearch] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [objectiveFilter, setObjectiveFilter] = useState<string>("all");
  const [referredByFilter, setReferredByFilter] = useState<string>("all");
  const [outcomeFilter, setOutcomeFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [orderBy, setOrderBy] = useState<string>("date");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  // Phones: the filters live in a bottom sheet.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const listTopRef = useRef<HTMLDivElement>(null);

  const fetchReferrals = () => {
    setLoading(true);
    axiosInstance
      .get(`/referral/pipeline`)
      .then((res) => {
        setReferrals(res.data.referrals || []);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [
    statusFilter,
    search,
    typeFilter,
    objectiveFilter,
    referredByFilter,
    outcomeFilter,
    minDaysFilter,
    dateFrom,
    dateTo,
  ]);

  // The days filter is the one filter that is URL-addressable (the dashboard
  // links into it), so keep the param in step without pushing history entries.
  const changeMinDaysFilter = (value: string) => {
    setMinDaysFilter(value);
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("minDays");
    else next.set("minDays", value);
    setSearchParams(next, { replace: true });
  };

  const handleStatusChange = (referral: any, newStatus: string) => {
    axiosInstance
      .post(`/referral/status`, {
        referral_id: referral._id,
        pipeline_status: newStatus,
      })
      .then(() => {
        setReferrals(
          referrals.map((item) =>
            item._id === referral._id
              ? { ...item, pipeline_status: newStatus }
              : item,
          ),
        );
      });
  };

  const outcomeSummary = (outcome: any) => {
    if (!outcome) return "—";
    const parts = [];
    if (outcome.assistance_provided === true) parts.push("Assisted");
    if (outcome.assistance_provided === false) parts.push("No assistance");
    if (outcome.objective_achieved === true) parts.push("Objective achieved");
    if (outcome.objective_achieved === false) parts.push("Objective not met");
    return parts.length ? parts.join(" · ") : "Recorded";
  };

  const uniqueSorted = (values: string[]) =>
    Array.from(
      new Set(values.map((v) => (v || "").trim()).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));

  const objectiveOptions = uniqueSorted(
    referrals.map((r) => r.referral_objective),
  );
  const referredByOptions = uniqueSorted(
    referrals.map((r) => r.referred_by_name),
  );
  const outcomeOptions = uniqueSorted(
    referrals.map((r) => outcomeSummary(r.outcome)),
  );

  const searchLower = search.trim().toLowerCase();
  const filtered = referrals.filter((r) => {
    if (searchLower) {
      const hay = `${r.firm_name || ""} ${r.referred_to || ""}`.toLowerCase();
      if (!hay.includes(searchLower)) return false;
    }
    if (typeFilter !== "all") {
      const t = r.referral_type === "Provider" ? "B2B" : "Resource";
      if (t !== typeFilter) return false;
    }
    if (
      objectiveFilter !== "all" &&
      (r.referral_objective || "").trim() !== objectiveFilter
    ) {
      return false;
    }
    if (
      referredByFilter !== "all" &&
      (r.referred_by_name || "").trim() !== referredByFilter
    ) {
      return false;
    }
    if (statusFilter === ACTIVE_STATUS_FILTER) {
      if (!IN_PIPELINE_STATUSES.includes(r.pipeline_status || "referred")) {
        return false;
      }
    } else if (
      statusFilter !== "all" &&
      (r.pipeline_status || "referred") !== statusFilter
    ) {
      return false;
    }
    if (minDaysFilter !== "all" && daysInPipeline(r) < Number(minDaysFilter)) {
      return false;
    }
    if (outcomeFilter !== "all" && outcomeSummary(r.outcome) !== outcomeFilter) {
      return false;
    }
    if (dateFrom || dateTo) {
      const dRaw = r.referral_date || r.created_at;
      if (!dRaw) return false;
      const d = dayjs(dRaw);
      if (dateFrom && d.isBefore(dateFrom, "day")) return false;
      if (dateTo && d.isAfter(dateTo, "day")) return false;
    }
    return true;
  });

  const countByStatus = (status: string) =>
    referrals.filter(
      (referral) => (referral.pipeline_status || "referred") === status,
    ).length;

  const filterDropdowns: {
    label: string;
    value: string;
    setValue: (v: string) => void;
    options: string[];
    getOptionLabel?: (v: string) => string;
  }[] = [
    {
      label: "Type",
      value: typeFilter,
      setValue: setTypeFilter,
      options: ["B2B", "Resource"],
    },
    {
      label: "Objective",
      value: objectiveFilter,
      setValue: setObjectiveFilter,
      options: objectiveOptions,
    },
    {
      label: "Referred By",
      value: referredByFilter,
      setValue: setReferredByFilter,
      options: referredByOptions,
    },
    {
      label: "Status",
      value: statusFilter,
      setValue: setStatusFilter,
      options: [ACTIVE_STATUS_FILTER, ...REFERRAL_PIPELINE_STATUSES],
      getOptionLabel: statusFilterLabel,
    },
    {
      label: "Days in pipeline",
      value: minDaysFilter,
      setValue: changeMinDaysFilter,
      options: MIN_DAYS_OPTIONS,
      getOptionLabel: (v) => `${v}+ days`,
    },
    {
      label: "Outcome",
      value: outcomeFilter,
      setValue: setOutcomeFilter,
      options: outcomeOptions,
    },
  ];

  const activeFilterCount =
    filterDropdowns.filter((d) => d.value !== "all").length +
    [dateFrom, dateTo].filter(Boolean).length;

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setObjectiveFilter("all");
    setReferredByFilter("all");
    setStatusFilter("all");
    setOutcomeFilter("all");
    changeMinDaysFilter("all");
    setDateFrom(null);
    setDateTo(null);
  };

  const filterControls = (fullWidth: boolean) => (
    <>
      {filterDropdowns.map((d) => (
        <FormControl
          key={d.label}
          size="small"
          fullWidth={fullWidth}
          sx={{ minWidth: 160 }}
        >
          <InputLabel>{d.label}</InputLabel>
          <Select
            label={d.label}
            value={d.value}
            onChange={(e: any) => d.setValue(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {d.options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {d.getOptionLabel ? d.getOptionLabel(opt) : opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}
      <DatePicker
        label="From"
        value={dateFrom}
        onChange={(v) => setDateFrom(v)}
        slotProps={{
          textField: { size: "small", fullWidth, sx: { minWidth: 150 } },
        }}
      />
      <DatePicker
        label="To"
        value={dateTo}
        onChange={(v) => setDateTo(v)}
        slotProps={{
          textField: { size: "small", fullWidth, sx: { minWidth: 150 } },
        }}
      />
    </>
  );

  const columns: {
    id: string;
    label: string;
    getValue: (r: any) => string | number;
  }[] = [
    { id: "business", label: "Business", getValue: (r) => r.firm_name || "" },
    {
      id: "referred_to",
      label: "Referred To",
      getValue: (r) => r.referred_to || "",
    },
    {
      id: "type",
      label: "Type",
      getValue: typeLabel,
    },
    {
      id: "objective",
      label: "Objective",
      getValue: (r) => r.referral_objective || "",
    },
    {
      id: "referred_by",
      label: "Referred By",
      getValue: (r) => (r.referred_by_name || "").trim(),
    },
    {
      id: "date",
      label: "Date",
      getValue: (r) => {
        const d = r.referral_date || r.created_at;
        return d ? new Date(d).getTime() : 0;
      },
    },
    {
      id: "status",
      label: "Status",
      getValue: (r) => r.pipeline_status || "referred",
    },
    {
      id: "outcome",
      label: "Outcome",
      getValue: (r) => outcomeSummary(r.outcome),
    },
  ];

  const handleSort = (colId: string) => {
    if (orderBy === colId) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(colId);
      setOrder("asc");
    }
    setPage(0);
  };

  const activeColumn = columns.find((c) => c.id === orderBy);
  const sorted = activeColumn
    ? [...filtered].sort((a, b) => {
        const av = activeColumn.getValue(a);
        const bv = activeColumn.getValue(b);
        let cmp: number;
        if (typeof av === "number" && typeof bv === "number") {
          cmp = av - bv;
        } else {
          cmp = String(av).localeCompare(String(bv));
        }
        return order === "asc" ? cmp : -cmp;
      })
    : filtered;
  const pageRows = sorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // A page picked from the bottom pager starts at the top of the list.
  const goToPage = (newPage: number) => {
    setPage(newPage);
    const listTop = listTopRef.current;
    if (listTop && listTop.getBoundingClientRect().top < 0) {
      listTop.scrollIntoView({ block: "start" });
    }
  };

  const pagination = (sx?: SxProps<Theme>) => (
    <TablePagination
      component="div"
      count={filtered.length}
      page={page}
      onPageChange={(_e, newPage) => goToPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
      rowsPerPageOptions={isPhone ? [] : [25, 50, 100]}
      sx={sx}
    />
  );

  const statusSelect = (referral: any, sx?: SxProps<Theme>) => (
    <FormControl size="small" sx={sx}>
      <Select
        value={referral.pipeline_status || "referred"}
        onChange={(e: any) => handleStatusChange(referral, e.target.value)}
        renderValue={(value: any) => <ReferralStatusChip status={value} />}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
      >
        {REFERRAL_PIPELINE_STATUSES.map((statusOption) => (
          <MenuItem key={statusOption} value={statusOption}>
            {pipelineStatusLabels[statusOption]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const emptyText = (
    <>
      No referrals
      {statusFilter !== "all"
        ? ` with status "${statusFilterLabel(statusFilter)}"`
        : ""}
      {minDaysFilter !== "all" ? ` older than ${minDaysFilter} days` : ""}.
    </>
  );

  return (
    <Box sx={{ padding: "20px", textAlign: "left" }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: "#2c2c34", mb: 2 }}
      >
        My Referrals
      </Typography>
      {/* Phones: one row of status chips that scrolls sideways. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 2,
          flexWrap: { xs: "nowrap", sm: "wrap" },
          overflowX: { xs: "auto", sm: "visible" },
        }}
        useFlexGap
      >
        <Chip
          label={`All (${referrals.length})`}
          onClick={() => setStatusFilter("all")}
          sx={{
            fontWeight: 600,
            flexShrink: { xs: 0, sm: 1 },
            backgroundColor: statusFilter === "all" ? PRIMARY : undefined,
            color: statusFilter === "all" ? "white" : undefined,
            "&:hover": {
              backgroundColor: statusFilter === "all" ? PRIMARY : undefined,
            },
          }}
        />
        {REFERRAL_PIPELINE_STATUSES.map((status) => {
          const color = pipelineStatusColors[status];
          const active = statusFilter === status;
          return (
            <Chip
              key={status}
              label={`${pipelineStatusLabels[status]} (${countByStatus(status)})`}
              onClick={() => setStatusFilter(active ? "all" : status)}
              sx={{
                fontWeight: 600,
                flexShrink: { xs: 0, sm: 1 },
                backgroundColor: active ? color : `${color}1f`,
                color: active ? "white" : color,
                "&:hover": { backgroundColor: active ? color : `${color}33` },
              }}
            />
          );
        })}
      </Stack>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {isPhone ? (
          <>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <TextField
                size="small"
                label="Search name"
                placeholder="Client / resource / provider"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFiltersOpen(true)}
                sx={{
                  flexShrink: 0,
                  height: 40,
                  color: PRIMARY,
                  borderColor: PRIMARY,
                  "&:hover": { borderColor: PRIMARY },
                }}
              >
                Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
              </Button>
            </Stack>
            <Drawer
              anchor="bottom"
              open={filtersOpen}
              onClose={() => setFiltersOpen(false)}
              PaperProps={{
                sx: {
                  maxHeight: "85vh",
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                  padding: "16px",
                  paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Filters
                </Typography>
                <Button onClick={clearFilters} sx={{ color: PRIMARY }}>
                  Clear all
                </Button>
              </Stack>
              <Stack spacing={2}>{filterControls(true)}</Stack>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setFiltersOpen(false)}
                sx={{
                  mt: 2,
                  backgroundColor: PRIMARY,
                  "&:hover": { backgroundColor: PRIMARY },
                }}
              >
                Show {filtered.length} referral
                {filtered.length === 1 ? "" : "s"}
              </Button>
            </Drawer>
          </>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            sx={{ mb: 2, flexWrap: "wrap" }}
            useFlexGap
            alignItems="center"
          >
            <TextField
              size="small"
              label="Search name"
              placeholder="Client / resource / provider"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            {filterControls(false)}
            <Button onClick={clearFilters} sx={{ color: PRIMARY }}>
              Clear
            </Button>
          </Stack>
        )}
      </LocalizationProvider>
      {loading ? (
        <Box sx={{ textAlign: "center", padding: "60px" }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : isPhone ? (
        <Box ref={listTopRef}>
          {pagination()}
          {filtered.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "#8a8a93", py: 3 }}>
              {emptyText}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {pageRows.map((referral) => (
                <Box
                  key={referral._id}
                  sx={{
                    border: "1px solid #ececf0",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    padding: "12px",
                  }}
                >
                  <Link
                    to={caseLink(referral)}
                    className="thrive-user-text"
                    style={{
                      color: PRIMARY,
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    {referral.firm_name || "Unknown Business"}
                  </Link>
                  <Typography
                    variant="body2"
                    className="thrive-user-text"
                    sx={{ color: "#2c2c34", mt: 0.5 }}
                  >
                    Referred to {referral.referred_to || "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#8a8a93" }}>
                    {typeLabel(referral)} · {referralDateLabel(referral)}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    useFlexGap
                    sx={{ mt: 0.5 }}
                  >
                    {/* Lines the status chip up with the text above. */}
                    {statusSelect(referral, { ml: "-14px" })}
                    {referral.outcome && (
                      <Typography
                        variant="body2"
                        sx={{ color: "#8a8a93", textAlign: "right" }}
                      >
                        {outcomeSummary(referral.outcome)}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
          {pagination()}
        </Box>
      ) : (
        <Paper
          ref={listTopRef}
          elevation={0}
          sx={{
            border: "1px solid #ececf0",
            borderRadius: "12px",
            overflow: "auto",
          }}
        >
          {pagination()}
          {/* The table scrolls on its own so both pagers stay in place. */}
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ "& th": { fontWeight: 700, color: "#2c2c34" } }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={orderBy === column.id ? order : false}
                      sx={column.id === "business" ? stickyColumnSx : undefined}
                    >
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{ textAlign: "center", color: "#8a8a93" }}
                    >
                      {emptyText}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((referral) => (
                    <TableRow key={referral._id} hover>
                      <TableCell sx={stickyColumnSx}>
                        <Link
                          to={caseLink(referral)}
                          style={{
                            color: PRIMARY,
                            fontWeight: 600,
                            textDecoration: "none",
                          }}
                        >
                          {referral.firm_name || "Unknown Business"}
                        </Link>
                      </TableCell>
                      <TableCell>{referral.referred_to || "—"}</TableCell>
                      <TableCell>{typeLabel(referral)}</TableCell>
                      <TableCell>
                        {referral.referral_objective || "—"}
                      </TableCell>
                      <TableCell>
                        {(referral.referred_by_name || "").trim() || "—"}
                      </TableCell>
                      <TableCell>{referralDateLabel(referral)}</TableCell>
                      <TableCell>{statusSelect(referral)}</TableCell>
                      <TableCell>{outcomeSummary(referral.outcome)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
          {/* Tablets: the pager again below the long table. */}
          {pagination({ display: { lg: "none" } })}
        </Paper>
      )}
    </Box>
  );
};

export default MyReferrals;
